import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Gear, Globe, Warning, Clock } from "@phosphor-icons/react";
import { useAuth } from "../auth/AuthContext";
import { switchableLocales } from "../i18n";
import { useAmendmentsCount } from "../hooks/useAmendments";
import { DriftHealthChip } from "./DriftHealthChip";
import { ThemeToggle } from "./ThemeToggle";
import { MyNameChip } from "./MyNameChip";

/**
 * v0.4.72 — Light SaaS header.
 *   left   : brand wordmark (clickable home link)
 *   center : status pills — jurisdictions · policy · drift · lang
 *   right  : amendments badge (admin), settings, user chip
 *
 * Type hierarchy:
 *   wordmark   text-xl semibold (was text-4xl black — inverted scale)
 *   tagline    text-xs slate-500 (was uppercase tracking-[0.2em] monospaced)
 *   pills      text-xs medium slate-600
 *   all caps   kept ONLY for the literal jurisdictions string (system tag)
 */
export const Header = () => {
  const nav = useNavigate();
  const auth = useAuth();
  const admin = auth?.admin;
  const { t, i18n } = useTranslation();

  const locales = switchableLocales();
  const isAdmin = admin?.role === "ADMIN";
  const { count: amendCount } = useAmendmentsCount();
  const showAmendBadge = isAdmin && amendCount > 0;

  return (
    <header
      data-testid="app-header"
      className="border-b border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur sticky top-0 z-30"
    >
      <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center gap-6">
        {/* Left — brand */}
        <button
          data-testid="header-home-link"
          onClick={() => nav("/")}
          className="flex items-center gap-2.5 hover:opacity-90 transition-opacity"
          title={t("ui.header.back_to_dashboard", { defaultValue: "Back to dashboard" })}
          aria-label={t("ui.header.back_to_dashboard", { defaultValue: "Back to dashboard" })}
        >
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center shadow-sm">
            <ShieldCheck size={18} weight="bold" className="text-white" />
          </div>
          <div className="leading-tight text-left">
            <div className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-50">
              GD VAT
              <span className="text-slate-400 dark:text-slate-500 font-normal text-sm">
                {" "}/ {t("ui.header.wordmark_suffix")}
              </span>
            </div>
            <div
              data-testid="header-tagline"
              className="text-xs text-slate-500 dark:text-slate-400"
            >
              {locales.length > 1
                ? t("ui.header.tagline_multilingual")
                : t("ui.header.tagline_en_only")}
            </div>
          </div>
        </button>

        {/* v0.5.0i+13 — center "EU OSS · UK VAT" + "Refuses to file
            if any check fails" pills removed. They were static
            decorative labels that didn't link anywhere or show
            live state; operator feedback: pointless. The
            DriftHealthChip (admin-only, real live state) survives
            in the right-side actions cluster below. */}
        <div className="hidden md:flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 flex-1 justify-center">
          {isAdmin && <DriftHealthChip isAdmin={isAdmin} />}
        </div>

        {/* Right — actions */}
        <div className="flex items-center gap-2 ml-auto">
          <ThemeToggle />
          {locales.length > 1 && (
            <select
              data-testid="header-language-switcher"
              value={i18n.language}
              onChange={(e) => {
                i18n.changeLanguage(e.target.value);
                try {
                  localStorage.setItem("gd_vat_locale", e.target.value);
                } catch {
                  // ignore
                }
              }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 text-slate-700 dark:text-slate-200 px-2.5 py-1 rounded-full text-xs font-medium"
              title={t("ui.common.language")}
            >
              {locales.map((loc) => (
                <option key={loc} value={loc}>
                  {loc.toUpperCase()}
                </option>
              ))}
            </select>
          )}
          {locales.length <= 1 && (
            <span
              data-testid="header-language-pending"
              className="border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-2.5 py-1 rounded-full text-xs text-slate-600 dark:text-slate-300 font-medium flex items-center gap-1"
              title={t("ui.header.locales_pending_tooltip")}
            >
              <Globe size={11} weight="bold" /> EN
            </span>
          )}
          {admin && (
            <button
              data-testid="header-history-button"
              onClick={() => nav("/history")}
              className="border border-slate-200 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 hover:text-teal-700 dark:hover:text-teal-300 dark:text-teal-300 text-slate-700 dark:text-slate-200 px-3 py-1 flex items-center gap-1.5 rounded-full transition-colors text-xs font-medium bg-white dark:bg-slate-900"
            >
              <Clock size={12} weight="bold" />
              {t("ui.header.history")}
            </button>
          )}
          {showAmendBadge && (
            <button
              data-testid="header-amendments-badge"
              onClick={() => nav("/amendments")}
              className="border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/40 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-800 dark:text-amber-200 px-3 py-1 flex items-center gap-1.5 rounded-full transition-colors text-xs font-medium"
              title={t("ui.header.amendments_tooltip", { defaultValue: "Pending amendments need operator action" })}
            >
              <Warning size={12} weight="bold" />
              {t("ui.header.amendments", { defaultValue: "Amendments" })}
              <span
                data-testid="header-amendments-count"
                className="ml-0.5 bg-amber-600 text-white font-semibold rounded-full px-1.5 text-xs"
              >
                {amendCount}
              </span>
            </button>
          )}
          {isAdmin && (
            <button
              data-testid="header-admin-button"
              onClick={() => nav("/settings")}
              className="border border-slate-200 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 hover:text-teal-700 dark:hover:text-teal-300 dark:text-teal-300 text-slate-700 dark:text-slate-200 px-3 py-1 flex items-center gap-1.5 rounded-full transition-colors text-xs font-medium bg-white dark:bg-slate-900"
            >
              <Gear size={12} weight="bold" />
              {t("ui.header.settings")}
            </button>
          )}
          {!admin && (
            <button
              data-testid="header-admin-button"
              onClick={() => nav("/login")}
              className="border border-slate-200 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 hover:text-teal-700 dark:hover:text-teal-300 dark:text-teal-300 text-slate-700 dark:text-slate-200 px-3 py-1 flex items-center gap-1.5 rounded-full transition-colors text-xs font-medium bg-white dark:bg-slate-900"
            >
              <Gear size={12} weight="bold" />
              {t("ui.header.admin")}
            </button>
          )}
          {/* operator 2026-10-18: click to set your own name */}
          {admin && <MyNameChip />}
        </div>
      </div>
    </header>
  );
};
